import { useEffect, useState, useMemo } from 'react';
import { historyAPI } from '../../../../services';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import ExportButton from '../../../../components/ExportButton';
import './History.css';

function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'closedAt', direction: 'desc' });
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });

  const [summary, setSummary] = useState({
    totalTrades: 0,
    totalCommission: 0,
    winRate: 0,
    winCount: 0,
    lossCount: 0,
    avgPnl: 0,
    totalPnl: 0,
    totalQuantity: 0,
    uniqueUserCount: 0,
    uniqueInstrumentCount: 0,
    currency: null // Set when filtered to single user
  });

  const [filterInputs, setFilterInputs] = useState({
    userId: '',
    instrument: '',
    type: '',
    minSize: '',
    maxSize: '',
    minPnl: '',
    maxPnl: '',
    dateRange: 'all',
    customStart: '',
    customEnd: '',
    limit: 50,
    search: ''
  });

  const [activeFilters, setActiveFilters] = useState({
    page: 1,
    limit: 50,
    userId: '',
    instrument: '',
    type: '',
    minSize: '',
    maxSize: '',
    minPnl: '',
    maxPnl: '',
    startDate: null,
    endDate: null
  });

  const activeFilterCount = useMemo(() => {
    const { page, limit, ...rest } = activeFilters;
    return Object.values(rest).filter(v => v && v !== null && v !== '').length;
  }, [activeFilters]);

  useEffect(() => {
    fetchHistory();
    fetchStats();
  }, [activeFilters]);

  const fetchHistory = async (overrides) => {
    try {
      setLoading(true);
      // Always include current sort; overrides take precedence
      const params = {
        ...activeFilters,
        sort: sortConfig.key,
        order: sortConfig.direction,
        ...(overrides || {})
      };
      console.log('[History] fetchHistory params:', { sort: params.sort, order: params.order, page: params.page });
      const response = await historyAPI.getAll(params);

      if (response.data.success && response.data.data) {
        const responseData = response.data.data;
        let histories = [];
        let paginationData = null;

        if (responseData.pagination) {
          histories = Array.isArray(responseData.data) ? responseData.data : [];
          paginationData = responseData.pagination;
        } else if (responseData.data && typeof responseData.data === 'object' && responseData.data.pagination) {
          histories = Array.isArray(responseData.data.data) ? responseData.data.data : [];
          paginationData = responseData.data.pagination;
        } else {
          histories = Array.isArray(responseData.data) ? responseData.data : [];
        }

        setHistory(histories);

        if (paginationData) {
          setPagination({
            page: paginationData.page || 1,
            limit: paginationData.limit || 50,
            total: paginationData.total || 0,
            pages: paginationData.pages || 0
          });
        }
      }
    } catch (error) {
      console.error('[History] Error fetching history:', error);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await historyAPI.getStats({
        userId: activeFilters.userId || undefined,
        instrument: activeFilters.instrument || undefined,
        type: activeFilters.type || undefined
      });

      let statsData = null;

      if (response.data.success && response.data.data) {
        if (response.data.data.data && Array.isArray(response.data.data.data) && response.data.data.data.length > 0) {
          statsData = response.data.data.data[0];
        } else if (Array.isArray(response.data.data) && response.data.data.length > 0) {
          statsData = response.data.data[0];
        } else if (typeof response.data.data === 'object') {
          statsData = response.data.data;
        }
      }

      if (statsData) {
        setSummary({
          totalTrades: statsData.totalTrades || 0,
          totalCommission: statsData.totalCommission || 0,
          winRate: statsData.winRate || 0,
          winCount: statsData.winCount || 0,
          lossCount: statsData.lossCount || 0,
          avgPnl: statsData.avgPnl || 0,
          totalPnl: statsData.totalPnl || 0,
          totalQuantity: statsData.totalQuantity || 0,
          uniqueUserCount: statsData.uniqueUserCount || 0,
          uniqueInstrumentCount: statsData.uniqueInstrumentCount || 0,
          currency: statsData.currency || null
        });
      }
    } catch (error) {
      console.error('[History] Error fetching stats:', error);
    }
  };

  // Server handles sorting — we receive already-sorted data
  const sortedHistory = useMemo(() => [...history], [history]);

  const filteredHistory = useMemo(() => {
    if (!filterInputs.search) return sortedHistory;
    const searchLower = filterInputs.search.toLowerCase();
    return sortedHistory.filter(trade =>
      trade.orderId?.toLowerCase().includes(searchLower) ||
      trade.instrument?.toLowerCase().includes(searchLower) ||
      trade.uid?.toLowerCase().includes(searchLower)
    );
  }, [sortedHistory, filterInputs.search]);

  const handleSort = (key) => {
    const newDirection = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    console.log('[History] handleSort:', { key, newDirection, prevKey: sortConfig.key, prevDir: sortConfig.direction });
    setSortConfig({ key, direction: newDirection });
    // Fetch directly with sort params — bypasses activeFilters effect chain
    fetchHistory({ page: 1, sort: key, order: newDirection });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFilterInputs(prev => ({ ...prev, [name]: value }));
    if (name === 'dateRange') applyDateRange(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') applyFilters();
  };

  const applyDateRange = (dateRange) => {
    const now = new Date();
    let startDate = null, endDate = null;
    switch (dateRange) {
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        endDate = new Date().toISOString();
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
        endDate = now.toISOString();
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate()).toISOString();
        endDate = now.toISOString();
        break;
      case '3months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()).toISOString();
        endDate = now.toISOString();
        break;
      case '6months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()).toISOString();
        endDate = now.toISOString();
        break;
      case '1year':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString();
        endDate = now.toISOString();
        break;
      default: break;
    }
    setActiveFilters(prev => ({ ...prev, startDate, endDate, page: 1 }));
  };

  const applyFilters = () => {
    const newFilters = {
      page: 1,
      limit: parseInt(filterInputs.limit) || 50,
      userId: filterInputs.userId || undefined,
      instrument: filterInputs.instrument || undefined,
      type: filterInputs.type || undefined,
      minSize: filterInputs.minSize ? parseFloat(filterInputs.minSize) : undefined,
      maxSize: filterInputs.maxSize ? parseFloat(filterInputs.maxSize) : undefined,
      minPnl: filterInputs.minPnl ? parseFloat(filterInputs.minPnl) : undefined,
      maxPnl: filterInputs.maxPnl ? parseFloat(filterInputs.maxPnl) : undefined
    };
    if (filterInputs.dateRange === 'custom') {
      // Use custom from/to dates
      setActiveFilters({
        ...newFilters,
        startDate: filterInputs.customStart ? new Date(filterInputs.customStart).toISOString() : null,
        endDate: filterInputs.customEnd ? new Date(filterInputs.customEnd).toISOString() : null
      });
    } else if (filterInputs.dateRange !== 'all') {
      applyDateRange(filterInputs.dateRange);
    } else {
      setActiveFilters({ ...newFilters, startDate: null, endDate: null });
    }
  };

  const handlePageChange = (newPage) => {
    setActiveFilters(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilterInputs({
      userId: '', instrument: '', type: '', minSize: '', maxSize: '',
      minPnl: '', maxPnl: '', dateRange: 'all', customStart: '', customEnd: '',
      limit: 50, search: ''
    });
    setActiveFilters({
      page: 1, limit: 50,
      userId: undefined, instrument: undefined,
      type: undefined, minSize: undefined, maxSize: undefined,
      minPnl: undefined, maxPnl: undefined, startDate: null, endDate: null
    });
  };

  const getTradeKey = (trade) => `${trade.uid || '?'}|${trade.orderId}|${trade.instrument}|${trade.openedAt}`;

  const toggleRowExpand = (trade) => {
    const key = getTradeKey(trade);
    const newExpanded = new Set(expandedRows);
    newExpanded.has(key) ? newExpanded.delete(key) : newExpanded.add(key);
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(date);
  };

  const formatPrice = (price) => {
    if (price === undefined || price === null) return '-';
    const num = Number(price);
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 5
    }).format(num);
  };

  const formatSize = (size) => {
    if (size === undefined || size === null) return '-';
    const num = Number(size);
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0, maximumFractionDigits: 2
    }).format(num);
  };

  const formatCompact = (num) => {
    const n = Number(num);
    if (isNaN(n)) return '0';
    const abs = Math.abs(n);
    if (!abs) return '0';
    if (abs >= 1000000) return (abs / 1000000).toFixed(1) + 'M';
    if (abs >= 1000) return (abs / 1000).toFixed(1) + 'K';
    return abs.toFixed(1);
  };

  // Currency helper — uses per-trade currency, falls back to stats currency, then USD
  const currencySymbolMap = { USD: '$', EUR: '€', GBP: '£', AUD: 'A$', JPY: '¥', CAD: 'C$', CHF: 'CHF', NZD: 'NZ$' };
  const getCurrencySymbol = (code) => currencySymbolMap[code] || code || '';
  const isSingleUserView = !!activeFilters.userId;


  const getDuration = (openedAt, closedAt) => {
    if (!openedAt || !closedAt) return '-';
    const diff = new Date(closedAt) - new Date(openedAt);
    if (diff <= 0) return '-';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    if (hours > 0) return `${hours}h ${minutes}m`;
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  const getProfitabilityRatio = () => {
    if (summary.lossCount === 0) return summary.winCount > 0 ? '∞' : '—';
    return (summary.winCount / summary.lossCount).toFixed(2);
  };

  // ---- Export columns & data ----
  const historyExportData = useMemo(() => {
    return filteredHistory.map(trade => {
      const totalFees = (trade.commission || 0) + (trade.swap || 0);
      return {
        orderId: trade.orderId || '',
        uid: trade.uid || '',
        instrument: trade.instrument || '',
        type: trade.type || '',
        duration: getDuration(trade.openedAt, trade.closedAt),
        openedAt: trade.openedAt || '',
        closedAt: trade.closedAt || '',
        entryPrice: trade.entryPrice,
        exitPrice: trade.exitPrice,
        size: trade.size,
        commission: trade.commission,
        swap: trade.swap,
        totalFees,
        grossPnl: trade.grossPnl,
        pnl: trade.pnl,
        currency: trade.currency || 'USD',
      };
    });
  }, [filteredHistory]);

  const historyExportColumns = [
    { key: 'orderId', header: 'Order ID' },
    { key: 'uid', header: 'User ID' },
    { key: 'instrument', header: 'Instrument' },
    { key: 'type', header: 'Type' },
    { key: 'duration', header: 'Duration' },
    { key: 'openedAt', header: 'Opened', format: (v) => v ? formatDate(v) : '—' },
    { key: 'closedAt', header: 'Closed', format: (v) => v ? formatDate(v) : '—' },
    { key: 'entryPrice', header: 'Entry Price', format: (v) => formatPrice(v), align: 'right' },
    { key: 'exitPrice', header: 'Exit Price', format: (v) => formatPrice(v), align: 'right' },
    { key: 'size', header: 'Size', format: (v) => formatSize(v), align: 'right' },
    { key: 'commission', header: 'Commission', format: (v) => formatPrice(v), align: 'right' },
    { key: 'swap', header: 'Swap', format: (v) => formatPrice(v), align: 'right' },
    { key: 'totalFees', header: 'Total Fees', format: (v) => formatPrice(v), align: 'right' },
    { key: 'grossPnl', header: 'Gross P&L', format: (v, row) => `${getCurrencySymbol(row?.currency)}${formatPrice(v)}`, align: 'right' },
    { key: 'pnl', header: 'Net P&L', format: (v, row) => `${getCurrencySymbol(row?.currency)}${formatPrice(v)}`, align: 'right' },
    { key: 'currency', header: 'Currency' },
  ];

  if (loading && history.length === 0) {
    return (
    <div className="history-container">
        <div className="content-header">
          <h2><i className="fas fa-history"></i> Trade History</h2>
          <p>View historical trading data and performance metrics.</p>
        </div>
        <LoadingSpinner
          message="Loading trade history data..."
          size="medium"
          className="history-spinner"
        />
      </div>
    );
  }

  return (
    <div className="history-container">
      {/* Header */}
      <div className="content-header">
        <h2><i className="fas fa-history"></i> Trade History</h2>
        <p>View historical trading data and performance metrics.</p>
      </div>

      {/* ====== COMPACT SUMMARY BAR ====== */}
      <div className="summary-bar">
        {isSingleUserView && summary.currency ? (
          <div className="summary-chip">
            <div className="chip-body">
              <span className="chip-label">Total P&L</span>
              <span className={`chip-value ${(summary.totalPnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(summary.totalPnl || 0) >= 0 ? '+' : ''}{getCurrencySymbol(summary.currency)}{formatCompact(summary.totalPnl)} {summary.currency}
              </span>
            </div>
          </div>
        ) : (
          <div className="summary-chip">
            <div className="chip-body">
              <span className="chip-label">Total P&L</span>
              <span className="chip-value" style={{ opacity: 0.5 }}>
                Filter by user to view
              </span>
            </div>
          </div>
        )}

        <div className="summary-chip">
          <div className="chip-body">
            <span className="chip-label">Win Rate</span>
            <span className="chip-value win-rate">
              {(summary.winRate || 0).toFixed(1)}%
              <span className="chip-meta">{summary.winCount}W / {summary.lossCount}L</span>
            </span>
          </div>
        </div>

        <div className="summary-chip">
          <div className="chip-body">
            <span className="chip-label">Trades</span>
            <span className="chip-value">
              {summary.totalTrades || 0}
              <span className="chip-meta">
                {summary.uniqueInstrumentCount} instr
                {summary.uniqueUserCount > 0 ? ` · ${summary.uniqueUserCount} users` : ''}
              </span>
            </span>
          </div>
        </div>

        {isSingleUserView && summary.currency ? (
          <div className="summary-chip">
            <div className="chip-body">
              <span className="chip-label">Avg P&L</span>
              <span className={`chip-value ${(summary.avgPnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(summary.avgPnl || 0) >= 0 ? '+' : ''}{getCurrencySymbol(summary.currency)}{formatPrice(summary.avgPnl)} {summary.currency}
              </span>
            </div>
          </div>
        ) : (
          <div className="summary-chip">
            <div className="chip-body">
              <span className="chip-label">Avg P&L</span>
              <span className="chip-value" style={{ opacity: 0.5 }}>
                Filter by user
              </span>
            </div>
          </div>
        )}

        {isSingleUserView && summary.currency ? (
          <div className="summary-chip">
            <div className="chip-body">
              <span className="chip-label">Commission</span>
              <span className="chip-value">{getCurrencySymbol(summary.currency)}{formatCompact(summary.totalCommission)} {summary.currency}</span>
            </div>
          </div>
        ) : (
          <div className="summary-chip">
            <div className="chip-body">
              <span className="chip-label">Commission</span>
              <span className="chip-value" style={{ opacity: 0.5 }}>
                Filter by user
              </span>
            </div>
          </div>
        )}

        <div className="summary-chip">
          <div className="chip-body">
            <span className="chip-label">Quantity</span>
            <span className="chip-value">{formatCompact(summary.totalQuantity)}</span>
          </div>
        </div>

        <div className="summary-chip">
          <div className="chip-body">
            <span className="chip-label">P/Ratio</span>
            <span className="chip-value">{getProfitabilityRatio()}</span>
          </div>
        </div>
      </div>

      {/* ====== FILTER BAR ====== */}
      <div className="history-filter-bar">
        <div className="history-filter-row">
          <div className="history-filter-field">
            <label>Search</label>
            <input
              type="text"
              name="search"
              placeholder="Order ID, Instrument, or User..."
              value={filterInputs.search}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
            />
          </div>
          <div className="history-filter-field">
            <label>Instrument</label>
            <input
              type="text"
              name="instrument"
              placeholder="e.g. EURUSD"
              value={filterInputs.instrument}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
            />
          </div>
          <div className="history-filter-field">
            <label>Type</label>
            <select name="type" value={filterInputs.type} onChange={handleInputChange}>
              <option value="">All</option>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
          <div className="history-filter-field">
            <label>User ID</label>
            <input
              type="text"
              name="userId"
              placeholder="User ID"
              value={filterInputs.userId}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
            />
          </div>
          <div className="history-filter-field">
            <label>Per Page</label>
            <select name="limit" value={filterInputs.limit} onChange={handleInputChange}>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>
          <div className="history-filter-field history-filter-actions">
            <ExportButton
              data={historyExportData}
              columns={historyExportColumns}
              filename="trade-history"
              title="Trade History"
              subtitle={`${pagination.total} trades • Exported on ${formatDate(new Date())}`}
              pdfOptions={{ orientation: 'landscape', fontSize: 7, padding: 2 }}
            />
            <button className="history-apply-btn" onClick={applyFilters}>
              <i className="fas fa-search"></i> Apply
            </button>
            {activeFilterCount > 0 && (
              <button className="history-clear-btn" onClick={clearFilters}>
                <i className="fas fa-times"></i> Clear
              </button>
            )}
          </div>
        </div>
        {activeFilterCount > 0 && (
          <div className="history-filter-hint">
            {activeFilterCount} active filter{activeFilterCount !== 1 ? 's' : ''} — {pagination.total} result{pagination.total !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* ====== TRADE CARD LIST ====== */}
      {loading && history.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner message="Updating..." size="small" />
        </div>
      )}

      <div className="history-list">
        {filteredHistory.length > 0 ? (
          filteredHistory.map((trade, idx) => {
            const tradeKey = getTradeKey(trade);
            const isExpanded = expandedRows.has(tradeKey);
            return (
              <div key={`${trade.uid || '?'}|${trade.orderId}|${trade.instrument}|${trade.openedAt}|${idx}`} className={`history-card ${isExpanded ? 'expanded' : ''}`}>
                <div className="history-card-main" onClick={() => toggleRowExpand(trade)}>
                  <div className="history-card-left">
                    <span className="history-instrument">{trade.instrument || '—'}</span>
                    <span className={`history-type ${trade.type?.toLowerCase()}`}>
                      {trade.type?.toUpperCase()}
                    </span>
                  </div>
                  <div className="history-card-center">
                    <span className="history-metric">Size {formatSize(trade.size)}</span>
                    <span className="history-metric">Entry {formatPrice(trade.entryPrice)}</span>
                    <span className="history-metric">Exit {formatPrice(trade.exitPrice)}</span>
                    <span className="history-metric">{getDuration(trade.openedAt, trade.closedAt)}</span>
                  </div>
                  <div className="history-card-right">
                    <span className="history-time">{formatDate(trade.closedAt)}</span>
                    <span className={`history-pnl ${trade.pnl >= 0 ? 'positive' : 'negative'}`}>
                      {trade.pnl >= 0 ? '+' : ''}{getCurrencySymbol(trade.currency)}{formatPrice(Math.abs(trade.pnl || 0))}
                    </span>
                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                  </div>
                </div>

                {isExpanded && (
                  <div className="history-expanded">
                    <div className="history-detail-grid">
                      <div className="history-detail-group">
                        <h5>Trade Details</h5>
                        <dl>
                          <div><dt>Order ID</dt><dd>{trade.orderId}</dd></div>
                          <div><dt>User ID</dt><dd>{trade.uid || '—'}</dd></div>
                          <div><dt>Instrument</dt><dd>{trade.instrument}</dd></div>
                          <div><dt>Type</dt><dd>{trade.type}</dd></div>
                          <div><dt>Duration</dt><dd>{getDuration(trade.openedAt, trade.closedAt)}</dd></div>
                          <div><dt>Opened</dt><dd>{formatDate(trade.openedAt)}</dd></div>
                          <div><dt>Closed</dt><dd>{formatDate(trade.closedAt)}</dd></div>
                        </dl>
                      </div>
                      <div className="history-detail-group">
                        <h5>Price &amp; Size</h5>
                        <dl>
                          <div><dt>Entry Price</dt><dd>{formatPrice(trade.entryPrice)}</dd></div>
                          <div><dt>Exit Price</dt><dd>{formatPrice(trade.exitPrice)}</dd></div>
                          <div><dt>Size</dt><dd>{formatSize(trade.size)}</dd></div>
                          <div><dt>P&amp;L</dt>
                            <dd className={trade.pnl >= 0 ? 'positive' : 'negative'}>
                              {trade.pnl >= 0 ? '+' : ''}{getCurrencySymbol(trade.currency)}{formatPrice(trade.pnl)} {trade.currency || 'USD'}
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div className="history-detail-group">
                        <h5>Fees &amp; Costs</h5>
                        <dl>
                          <div><dt>Commission</dt><dd>{getCurrencySymbol(trade.currency)}{formatPrice(trade.commission || 0)}</dd></div>
                          <div><dt>Swap</dt><dd>{getCurrencySymbol(trade.currency)}{formatPrice(trade.swap || 0)}</dd></div>
                          <div><dt>Total Fees</dt><dd>-{getCurrencySymbol(trade.currency)}{formatPrice((trade.commission || 0) + (trade.swap || 0))}</dd></div>
                          <div><dt>Gross P&amp;L</dt>
                            <dd className={trade.grossPnl != null ? (trade.grossPnl >= 0 ? 'positive' : 'negative') : ((trade.pnl ?? 0) >= 0 ? 'positive' : 'negative')}>
                              {trade.grossPnl != null
                                ? `${trade.grossPnl >= 0 ? '+' : ''}${getCurrencySymbol(trade.currency)}${formatPrice(trade.grossPnl)}`
                                : '—'}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="history-empty">
            <i className="fas fa-inbox"></i>
            <h4>No trades found</h4>
            <p>Try adjusting your filters or date range.</p>
            {activeFilterCount > 0 && (
              <button className="retry-btn" onClick={clearFilters}>
                <i className="fas fa-times"></i> Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* ====== PAGINATION ====== */}
      {pagination.pages > 1 && (
        <div className="history-pagination">
          <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1}>
            <i className="fas fa-chevron-left"></i>
          </button>
          <span>Page {pagination.page} of {pagination.pages} · {pagination.total} total</span>
          <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.pages}>
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

    </div>
  );
}

export default History;
